import type { ReactNode } from 'react';
import { MessagesSquare, ShieldCheck, Video } from 'lucide-react';
import { BrandMark } from '@/components/common/BrandMark';

interface AuthShellProps {
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}

const HIGHLIGHTS = [
  { icon: MessagesSquare, title: 'Chat that keeps up', body: 'Threads, channels and files, all in one place.' },
  { icon: Video, title: 'Calls and meetings in HD', body: 'Screen sharing and recordings, built in.' },
  { icon: ShieldCheck, title: 'Secure by design', body: 'Your organisation’s data stays your organisation’s.' },
] as const;

/**
 * The frame every signed-out page shares: a showcase on the left (wide screens
 * only) and the form on a raised card on the right. Decorative layers are pure
 * CSS - no image request stands between anyone and the sign-in form.
 */
export function AuthShell({ title, subtitle, children, footer }: AuthShellProps) {
  return (
    <main className="relative flex min-h-screen w-full overflow-hidden bg-background">
      <aside className="relative hidden w-[46%] max-w-[620px] flex-col justify-between overflow-hidden bg-brand-gradient p-12 text-white lg:flex">
        <span aria-hidden className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/20 blur-3xl" />
        <span aria-hidden className="pointer-events-none absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-brand-300/30 blur-3xl" />
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.14] [background-image:radial-gradient(white_1px,transparent_1px)] [background-size:22px_22px]"
        />

        <BrandMark variant="mono" size={36} withWordmark className="relative text-white" />

        <div className="relative space-y-8">
          <h2 className="max-w-md text-4xl font-semibold leading-tight tracking-tight text-white">
            Where your team talks, meets and gets work done.
          </h2>
          <ul className="grid max-w-md gap-3">
            {HIGHLIGHTS.map(({ icon: Icon, title: heading, body }) => (
              <li
                key={heading}
                className="flex items-start gap-3.5 rounded-2xl bg-white/10 p-4 ring-1 ring-inset ring-white/15 backdrop-blur-sm"
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/20">
                  <Icon size={18} aria-hidden />
                </span>
                <span>
                  <span className="block text-md font-semibold">{heading}</span>
                  <span className="mt-0.5 block text-base text-white/75">{body}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-sm text-white/70">© Cyphex Technologies</p>
      </aside>

      <section className="relative flex flex-1 items-center justify-center px-5 py-12">
        <span aria-hidden className="pointer-events-none absolute -right-24 -top-40 h-80 w-80 rounded-full bg-brand-400/20 blur-3xl" />
        <span aria-hidden className="pointer-events-none absolute -bottom-32 left-0 h-72 w-72 rounded-full bg-brand-300/20 blur-3xl" />

        <div className="relative w-full max-w-[440px] rounded-3xl border border-border/70 bg-card/90 p-8 shadow-modal backdrop-blur-xl sm:p-10">
          <BrandMark size={40} className="mb-8" />
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">{title}</h1>
          <p className="mt-2 text-md text-muted-foreground">{subtitle}</p>
          <div className="mt-8">{children}</div>
          {footer ? (
            <div className="mt-8 border-t border-border pt-6 text-center text-base text-muted-foreground">
              {footer}
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
